import api from './api';

/**
 * Servico para gerenciar consultas de estoque APENAS do Mercado Livre
 */
class MlStockService {
    constructor() {
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutos
        this.maxBatchSize = 200;
    }

    _normalizeSkuKey(value) {
        const raw = String(value || '').trim();
        if (!raw) {
            return '';
        }

        try {
            return decodeURIComponent(raw).toUpperCase();
        } catch {
            const escaped = raw.replace(/%(?![0-9A-Fa-f]{2})/g, '%25');
            try {
                return decodeURIComponent(escaped).toUpperCase();
            } catch {
                return raw.toUpperCase();
            }
        }
    }

    _unwrapPayload(response) {
        if (response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'data')) {
            return response.data;
        }
        return response;
    }

    /**
     * Busca estoque ML para uma lista de produtos (por itemId)
     * @param {Array} products - Lista de produtos com itemId
     * @returns {Promise<Map>} Mapa itemId -> available_quantity
     */
    async getStockForProducts(products) {
        if (!products || products.length === 0) {
            return new Map();
        }

        const mlProducts = products.filter((p) => this._isMlProduct(p));

        if (mlProducts.length < products.length) {
            console.warn(
                ` [MLStock] ${products.length - mlProducts.length} produtos nao-ML foram filtrados. Use BlingStockService para produtos Bling.`,
            );
        }

        const skus = [
            ...new Set(
                mlProducts
                    .map((product) => {
                        if (!product || typeof product !== 'object') {
                            return null;
                        }
                        return product.seller_sku || product.sku || product.item_seller_sku || null;
                    })
                    .filter(Boolean),
            ),
        ];
        if (skus.length === 0) {
            return new Map();
        }
        return this.getStockBySkus(skus);
    }

    /**
     * Busca estoque ML por seller_skus
     * @param {Array<string>} sellerSkus - SKUs dos produtos ML
     * @returns {Promise<Map>} Mapa sellerSku -> dados de estoque
     */
    async getStockBySkus(sellerSkus) {
        if (!sellerSkus || sellerSkus.length === 0) {
            return new Map();
        }

        const uniqueSkus = [...new Set(sellerSkus.map((sku) => String(sku || '').trim()).filter(Boolean))];
        if (uniqueSkus.length === 0) {
            return new Map();
        }

        // Verificar cache
        const cachedData = this._getCachedData(uniqueSkus);
        const uncachedSkus = uniqueSkus.filter((sku) => {
            const normalized = this._normalizeSkuKey(sku);
            return !cachedData.has(sku) && (!normalized || !cachedData.has(normalized));
        });

        // Se todos os dados estao em cache, retornar
        if (uncachedSkus.length === 0) {
            return cachedData;
        }

        try {
            const freshMap = new Map();

            for (let index = 0; index < uncachedSkus.length; index += this.maxBatchSize) {
                const batch = uncachedSkus.slice(index, index + this.maxBatchSize);
                const response = await api.post('/stock/batch', {
                    seller_skus: batch,
                });
                const payload = this._unwrapPayload(response);
                if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
                    continue;
                }

                Object.entries(payload).forEach(([sku, entry]) => {
                    const mlData = entry?.mercadolivre || {};

                    // Compatibilidade: se for numero antigo, converter
                    const isOldFormat = typeof entry?.mercadolivre === 'number';
                    const total = isOldFormat ? entry.mercadolivre : mlData.total || mlData.available_quantity || 0;
                    const available = isOldFormat ? entry.mercadolivre : mlData.available_quantity || 0;
                    const notAvailable = isOldFormat ? 0 : mlData.not_available_quantity || 0;
                    const details = isOldFormat ? [] : mlData.not_available_detail || [];
                    const inventoryId = isOldFormat ? null : mlData.inventory_id || null;

                    const data = {
                        quantity: available, // Compatibilidade: manter quantity como available_quantity
                        total: total,
                        available_quantity: available,
                        inventory_id: inventoryId,
                        not_available_quantity: notAvailable,
                        not_available_detail: details,
                        status: available > 0 ? 'available' : 'out-of-stock',
                        source: 'normalized',
                        lastUpdated: new Date().toISOString(),
                    };

                    const normalizedSku = this._normalizeSkuKey(sku);
                    freshMap.set(sku, data);
                    if (normalizedSku) {
                        freshMap.set(normalizedSku, data);
                        this._updateCache(normalizedSku, data);
                    }

                    this._updateCache(sku, data);
                });
            }

            const combinedMap = new Map([...cachedData, ...freshMap]);
            for (const sku of uniqueSkus) {
                if (combinedMap.has(sku)) {
                    continue;
                }

                const normalized = this._normalizeSkuKey(sku);
                if (normalized && combinedMap.has(normalized)) {
                    combinedMap.set(sku, combinedMap.get(normalized));
                }
            }

            return combinedMap;
        } catch (error) {
            console.error(' [MLStock] Erro ao buscar estoques por SKU:', error);

            // Retornar dados do cache se disponiveis
            if (cachedData.size > 0) {
                console.warn(` [MLStock] Retornando ${cachedData.size} SKUs do cache devido a erro`);
                return cachedData;
            }

            return new Map();
        }
    }

    /**
     * Verifica se um produto e do Mercado Livre
     * @param {Object} product - Produto a verificar
     * @returns {boolean} true se for produto ML
     */
    _isMlProduct(product) {
        if (!product || typeof product !== 'object') {
            return false;
        }
        return !!(product.itemId || product.item_id || product.mlItemId);
    }

    /**
     * Busca dados do cache para os SKUs fornecidos
     * @param {Array<string>} skus - Lista de SKUs
     * @returns {Map} Dados em cache
     */
    _getCachedData(skus) {
        const cachedMap = new Map();
        const now = Date.now();

        skus.forEach((sku) => {
            const cached = this.cache.get(sku);
            if (cached && now - cached.timestamp < this.cacheTimeout && !cachedMap.has(sku)) {
                cachedMap.set(sku, cached.data);
            }

            const normalized = this._normalizeSkuKey(sku);
            if (normalized && normalized !== sku) {
                const normalizedCached = this.cache.get(normalized);
                if (normalizedCached && now - normalizedCached.timestamp < this.cacheTimeout && !cachedMap.has(sku)) {
                    cachedMap.set(sku, normalizedCached.data);
                }
            }
        });

        return cachedMap;
    }

    /**
     * Atualiza cache para um SKU
     * @param {string} sku - SKU do produto
     * @param {Object} data - Dados de estoque
     */
    _updateCache(sku, data) {
        this.cache.set(sku, {
            data,
            timestamp: Date.now(),
        });
    }

    /**
     * Busca estoque no Mercado Livre por um identificador (SKU ou MLB ID)
     * @param {string} identifier - SKU ou MLB ID
     * @returns {Promise<Object|null>} Dados de estoque
     */
    async getStockByIdentifier(identifier) {
        if (!identifier) {
            return null;
        }

        const cacheKey = `ml_stock_${identifier}`;
        const cached = this.cache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
            return cached.data;
        }

        try {
            const encodedIdentifier = encodeURIComponent(identifier);
            const response = await api.get(`/stock/ml/${encodedIdentifier}`);
            const payload = this._unwrapPayload(response);
            if (payload && typeof payload === 'object') {
                this.cache.set(cacheKey, { data: payload, timestamp: Date.now() });
                return payload;
            }
            return null;
        } catch (error) {
            console.error(`[MLStock] Erro ao buscar estoque para o identificador ${identifier}:`, error);
            return null;
        }
    }

    /**
     * Busca estoque para uma lista de identificadores (SKU ou MLB ID)
     * @param {Array<string>} identifiers - Lista de identificadores
     * @returns {Promise<Map>} Mapa identifier -> dados de estoque
     */
    async getBatchStockByIdentifiers(identifiers) {
        if (!identifiers || identifiers.length === 0) {
            return new Map();
        }

        // Normalizar para SKUs (quando possivel)
        return this.getStockBySkus(identifiers);
    }

    /**
     * Limpa o cache
     */
    clearCache() {
        this.cache.clear();
        console.log(' [MLStock] Cache limpo');
    }
}

const mlStockService = new MlStockService();
export default mlStockService;
